/**
 * FDV — avisos suaves al desbloquear regalos del carrito.
 * Doc 15 §3.6 (motor de regalos, etapa Carrito).
 *
 * Toast(s) abajo-centro con el mensaje de cada regalo, para que el cliente
 * SIEMPRE sepa qué pasó:
 *   · pares producto→regalo (`kind: 'binary'`, `trigger_type: product_in_cart`,
 *     p. ej. vela "La Luna" → carta del tarot) → solo el toast.
 *   · umbrales (`kind: 'threshold'`, envío gratis $699 / vela del zodiaco $999)
 *     → el toast + el confeti de `fdv-gift-celebrate.js` (el pico emocional).
 *
 * Si se desbloquea **más de un regalo a la vez** (p. ej. agregas la vela "La
 * Luna" y con eso cruzas $699), se muestran **apilados** — un toast por premio,
 * cada uno con su propio auto-descarte. (Antes: el 2º `show()` pisaba al 1º y
 * solo se veía uno.)
 *
 * Escucha `fdv:gift:unlocked` (evento DOM del motor, patch #24). Respeta
 * `prefers-reduced-motion` (aparece sin animar). Usa la Popover API para quedar
 * por encima del cart drawer de Horizon (`<dialog>.showModal()`); sin soporte,
 * cae a `position:fixed` normal.
 *
 * Se carga global desde snippets/fdv-gift-engine.liquid; inerte hasta el evento.
 */

const REDUCED = (() => {
  try {
    return matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
})();

const SHOW_MS = 4600;
const LEAVE_MS = 260;
const COLLECT_MS = 160; // ventana para agrupar desbloqueos simultáneos
const MAX_VISIBLE = 3;

let stack = null;
let styled = false;
let pending = [];
let flushTimer = 0;

function injectStyles() {
  if (styled || document.getElementById('fdv-gift-toast-styles')) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'fdv-gift-toast-styles';
  s.textContent = `
  .fdv-gift-toast-stack{
    position:fixed; margin:0; inset:auto; left:50%; bottom:24px;
    transform:translateX(-50%);
    display:flex; flex-direction:column-reverse; align-items:center; gap:8px;
    width:max-content; max-width:min(92vw,420px);
    background:transparent; border:0; padding:0; overflow:visible;
    z-index:2147483002;
  }
  .fdv-gift-toast-stack::backdrop{background:transparent}
  .fdv-gift-toast{
    box-sizing:border-box; width:100%; padding:12px 20px;
    background:#3b2a4d; color:#efe6d2; text-align:center;
    font-family:var(--fdv-font-body,var(--font-body-family,inherit));
    font-size:14px; font-weight:500; line-height:1.4; letter-spacing:.01em;
    border:1px solid rgba(200,158,52,.45); border-radius:12px;
    box-shadow:0 8px 28px rgba(24,16,32,.32);
    opacity:0; transform:translateY(10px);
    transition:opacity .24s ease, transform .24s ease;
  }
  .fdv-gift-toast.is-visible{opacity:1; transform:translateY(0)}
  .fdv-gift-toast.is-leaving{opacity:0; transform:translateY(10px)}
  @media (prefers-reduced-motion: reduce){
    .fdv-gift-toast{transition:none; transform:none}
  }`;
  document.head.appendChild(s);
}

function ensureStack() {
  if (stack && document.body.contains(stack)) return stack;
  injectStyles();
  stack = document.createElement('div');
  stack.className = 'fdv-gift-toast-stack';
  stack.setAttribute('role', 'status');
  stack.setAttribute('aria-live', 'polite');
  try {
    stack.setAttribute('popover', 'manual');
  } catch {
    /* atributo no soportado → fallback fixed */
  }
  document.body.appendChild(stack);
  return stack;
}

function openStack() {
  if (typeof stack.showPopover === 'function' && stack.hasAttribute('popover') && !stack.matches(':popover-open')) {
    try {
      stack.showPopover();
    } catch {
      /* ya abierto */
    }
  }
}

function closeStackIfEmpty() {
  if (!stack || stack.childElementCount > 0) return;
  if (typeof stack.hidePopover === 'function' && stack.matches(':popover-open')) {
    try {
      stack.hidePopover();
    } catch {
      /* noop */
    }
  }
}

function addToast(message) {
  ensureStack();

  // cap: si ya hay MAX_VISIBLE, retira el más viejo antes de meter el nuevo
  while (stack.childElementCount >= MAX_VISIBLE && stack.firstElementChild) {
    stack.firstElementChild.remove();
  }

  const t = document.createElement('div');
  t.className = 'fdv-gift-toast';
  t.textContent = message;
  stack.appendChild(t);
  openStack();

  const enter = () => t.classList.add('is-visible');
  if (REDUCED) enter();
  else requestAnimationFrame(() => requestAnimationFrame(enter));

  const remove = () => {
    if (!t.isConnected) return;
    t.classList.remove('is-visible');
    t.classList.add('is-leaving');
    const drop = () => {
      t.remove();
      closeStackIfEmpty();
    };
    if (REDUCED) drop();
    else setTimeout(drop, LEAVE_MS);
  };
  setTimeout(remove, SHOW_MS);
}

/* --------------------------------------------------------------- eventos -- */

function messageFor(offer) {
  if (offer.kind === 'binary') {
    const label = offer.giftLineLabel || 'un regalo';
    return offer.unlockedHeadline || `Añadimos ${label} a tu carrito — es un regalo 🎁`;
  }
  // threshold
  return offer.unlockedHeadline || '✨ ¡Regalo desbloqueado! Lo agregamos a tu carrito.';
}

function onUnlocked(e) {
  const offer = e && e.detail && e.detail.offer;
  if (!offer) return;

  // discount_code / url_param también son binary, pero su feedback es la barra
  // de anuncio / el reveal de la campaña, no un toast en el carrito.
  if (offer.kind === 'binary' && offer._offer?.triggerType && offer._offer.triggerType !== 'product_in_cart') return;
  if (offer.kind !== 'binary' && offer.kind !== 'threshold') return;

  // Agrupa los desbloqueos que llegan casi juntos (mismo cambio de carrito) para
  // apilarlos, dedupe por id.
  if (!pending.some((o) => o.id === offer.id)) pending.push(offer);
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, COLLECT_MS);
}

function flush() {
  const offers = pending;
  pending = [];
  // Umbrales primero (el confeti va con ellos), luego los pares producto→regalo.
  offers.sort((a, b) => (a.kind === 'threshold' ? 0 : 1) - (b.kind === 'threshold' ? 0 : 1));
  offers.forEach((offer, i) => {
    if (i === 0 || REDUCED) addToast(messageFor(offer));
    else setTimeout(() => addToast(messageFor(offer)), i * 140); // entrada escalonada
  });
}

document.addEventListener('fdv:gift:unlocked', onUnlocked);
